import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Minus,
  Plus,
  X,
  ShoppingBag,
  ChevronRight,
  Heart,
  Truck,
  Tag,
  ChevronDown,
  Check,
  Loader2,
  Star,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

// Breadcrumbs Component
const Breadcrumbs = () => {
  return (
    <motion.nav
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex items-center gap-2 text-sm text-muted-foreground mb-6"
    >
      <Link to="/" className="hover:text-foreground transition-colors">
        Home
      </Link>
      <ChevronRight className="w-4 h-4" />
      <span className="text-foreground font-medium">Your Cart</span>
    </motion.nav>
  );
};

// Cart Item Component
const CartItem = ({ item, index, onRemove, onUpdateQuantity }) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(item.id, item.selectedSize, item.selectedColor);
    }, 200);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isRemoving ? 0 : 1, y: 0, scale: isRemoving ? 0.95 : 1 }}
      exit={{ opacity: 0, x: -100, transition: { duration: 0.3 } }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="relative bg-card rounded-[24px] overflow-hidden shadow-soft group"
    >
      {/* Badge */}
      <div className="absolute top-4 right-4 z-10">
        <Badge className="bg-foreground text-background rounded-full px-3 py-1 text-xs font-medium">
          Offers
        </Badge>
      </div>

      <div className="p-4 sm:p-6">
        {/* Product Image */}
        <div className="relative aspect-[4/3] bg-muted/30 rounded-2xl overflow-hidden mb-4">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>

        {/* Product Info */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-foreground text-base leading-tight line-clamp-1">
              {item.name}
            </h3>
            <span className="font-bold text-foreground text-lg whitespace-nowrap">
              ${item.price.toFixed(2)}
            </span>
          </div>

          {/* Rating */}
          {item.rating && (
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 fill-warning text-warning" />
              <span className="text-sm font-medium text-foreground">{item.rating}</span>
              <span className="text-sm text-muted-foreground">
                ({item.reviews} Reviews)
              </span>
            </div>
          )}

          {/* Selected Options */}
          {(item.selectedSize || item.selectedColor) && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {item.selectedColor && (
                <div className="flex items-center gap-1">
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: item.selectedColor.hex }}
                  />
                  <span>{item.selectedColor.name}</span>
                </div>
              )}
              {item.selectedSize && <span>Size: {item.selectedSize}</span>}
            </div>
          )}

          {/* Quantity & Actions */}
          <div className="flex items-center justify-between gap-2 pt-2">
            {/* Quantity Controls */}
            <div className="flex items-center gap-2 bg-muted/50 rounded-full p-1">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() =>
                  onUpdateQuantity(
                    item.id,
                    item.selectedSize,
                    item.selectedColor,
                    item.quantity - 1
                  )
                }
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background transition-colors"
              >
                <Minus className="w-4 h-4" />
              </motion.button>
              <span className="w-8 text-center font-medium">{item.quantity}</span>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() =>
                  onUpdateQuantity(
                    item.id,
                    item.selectedSize,
                    item.selectedColor,
                    item.quantity + 1
                  )
                }
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background transition-colors"
              >
                <Plus className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Remove Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleRemove}
              className="p-2 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            >
              <X className="w-5 h-5" />
            </motion.button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 rounded-full border-border hover:bg-muted transition-colors"
              onClick={handleRemove}
            >
              Remove
            </Button>
            <motion.div whileTap={{ scale: 0.95 }} className="flex-1">
              <Button
                size="sm"
                className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Buy Now
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Order Summary Component
const OrderSummary = ({ subtotal, shipping, tax, total, itemCount, onCheckout }) => {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsCheckingOut(false);
    setIsSuccess(true);
    toast.success('Order placed successfully!', {
      description: 'Thank you for your purchase.',
    });
    setTimeout(() => {
      setIsSuccess(false);
      onCheckout();
    }, 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-card rounded-[32px] p-6 lg:p-8 shadow-lifted sticky top-24"
    >
      <h2 className="font-display font-semibold text-xl text-foreground mb-6">
        Order Summary
      </h2>

      {/* Price Breakdown */}
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal ({itemCount} items)</span>
          <span className="font-medium text-foreground">${subtotal.toFixed(2)}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Truck className="w-4 h-4" />
            <span>Shipping Estimate</span>
          </div>
          <span className="font-medium text-foreground">
            {shipping === 0 ? (
              <span className="text-success">Free</span>
            ) : (
              `$${shipping.toFixed(2)}`
            )}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Tax Estimate</span>
          <span className="font-medium text-foreground">${tax.toFixed(2)}</span>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <span className="font-semibold text-foreground">Total</span>
          <span className="font-bold text-xl text-foreground">${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Free Shipping Notice */}
      {subtotal < 50 && subtotal > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 bg-muted/50 rounded-xl text-sm text-muted-foreground"
        >
          <Truck className="w-4 h-4 inline mr-2" />
          Add ${(50 - subtotal).toFixed(2)} more for free shipping!
        </motion.div>
      )}

      {/* Discount Code */}
      <div className="mt-6">
        <button
          onClick={() => setIsDiscountOpen(!isDiscountOpen)}
          className="flex items-center justify-between w-full text-sm font-medium text-foreground"
        >
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            <span>Discount Code</span>
          </div>
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isDiscountOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {isDiscountOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-2 mt-3">
                <Input
                  type="text"
                  placeholder="Enter code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  className="flex-1 h-10 rounded-xl"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl h-10 px-4"
                  onClick={() => {
                    toast.info('Discount code applied!');
                    setDiscountCode('');
                  }}
                >
                  Apply
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Checkout Button */}
      <motion.button
        onClick={handleCheckout}
        disabled={isCheckingOut || isSuccess || itemCount === 0}
        className={`w-full h-14 rounded-full font-medium text-base mt-6 flex items-center justify-center gap-2 transition-all ${
          isSuccess
            ? 'bg-success text-success-foreground'
            : 'bg-foreground text-background hover:bg-foreground/90'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        whileTap={{ scale: 0.98 }}
      >
        <AnimatePresence mode="wait">
          {isCheckingOut ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Loader2 className="w-5 h-5 animate-spin" />
            </motion.div>
          ) : isSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
              <Check className="w-5 h-5" />
              <span>Order Placed!</span>
            </motion.div>
          ) : (
            <motion.div
              key="default"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
              <Check className="w-5 h-5" />
              <span>Proceed to Checkout</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Wishlist Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        className="w-full h-12 rounded-full border-2 border-border font-medium text-sm mt-3 flex items-center justify-center gap-2 hover:bg-muted transition-colors"
      >
        <Heart className="w-4 h-4" />
        <span>Save for Later</span>
      </motion.button>

      {/* Trust Badges */}
      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          <Truck className="w-4 h-4 inline mr-1" />
          Free delivery for orders
        </p>
      </div>
    </motion.div>
  );
};

// Empty Cart Component
const EmptyCart = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-32 h-32 bg-muted rounded-full flex items-center justify-center mb-6"
      >
        <ShoppingBag className="w-16 h-16 text-muted-foreground" />
      </motion.div>

      <h2 className="font-display font-semibold text-2xl text-foreground mb-2">
        Your cart is empty
      </h2>
      <p className="text-muted-foreground max-w-sm mb-8">
        Looks like you haven&apos;t added anything to your cart yet. Start shopping to
        fill it up!
      </p>

      <motion.div whileTap={{ scale: 0.98 }}>
        <Button
          onClick={() => navigate('/')}
          className="rounded-full px-8 h-12 bg-foreground text-background hover:bg-foreground/90"
        >
          Continue Shopping
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Main Cart Page Component
const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    cartItemsCount,
    cartSubtotal,
    shippingEstimate,
    taxEstimate,
    cartTotal,
    removeFromCart,
    updateQuantity,
    clearCart,
    isCartAnimating,
  } = useCart();

  const handleCheckout = () => {
    clearCart();
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-2 cursor-pointer"
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/')}
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-display font-bold text-sm">
                  S
                </span>
              </div>
              <span className="font-display font-bold text-xl text-foreground">
                StuffLab
              </span>
            </motion.div>

            {/* Search */}
            <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="Search"
                  className="w-full h-10 pl-4 pr-10 rounded-full bg-muted/50 border-0"
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <motion.div
                animate={isCartAnimating ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                <Button variant="ghost" size="icon" className="relative rounded-full">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                  {cartItemsCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-warning text-foreground text-xs rounded-full">
                      {cartItemsCount}
                    </Badge>
                  )}
                </Button>
              </motion.div>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Heart className="w-5 h-5" />
              </Button>
              <div className="w-8 h-8 rounded-full bg-muted overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100"
                  alt="User"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Page Title */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-display font-bold text-4xl md:text-5xl text-foreground mb-2"
        >
          Your Cart
        </motion.h1>

        {/* Breadcrumbs */}
        <Breadcrumbs />

        {cartItems.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-8 lg:gap-12">
            {/* Cart Items */}
            <div>
              {/* Cart Items Grid */}
              <motion.div
                layout
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6"
              >
                <AnimatePresence mode="popLayout">
                  {cartItems.map((item, index) => (
                    <CartItem
                      key={`${item.id}-${item.selectedSize}-${item.selectedColor?.name}`}
                      item={item}
                      index={index}
                      onRemove={removeFromCart}
                      onUpdateQuantity={updateQuantity}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>

              {/* Bottom Actions */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap items-center gap-4 mt-8"
              >
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate('/')}
                >
                  Continue Shopping
                </Button>
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full"
                  onClick={() => {
                    clearCart();
                    toast.success('Cart cleared');
                  }}
                >
                  Clear Cart
                </Button>
              </motion.div>
            </div>

            {/* Order Summary - Desktop */}
            <div className="hidden lg:block">
              <OrderSummary
                subtotal={cartSubtotal}
                shipping={shippingEstimate}
                tax={taxEstimate}
                total={cartTotal}
                itemCount={cartItemsCount}
                onCheckout={handleCheckout}
              />
            </div>
          </div>
        )}
      </main>

      {/* Mobile Order Summary - Fixed at bottom */}
      {cartItems.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 shadow-lifted z-40">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-muted-foreground">Total ({cartItemsCount} items)</p>
                <p className="font-bold text-xl text-foreground">${cartTotal.toFixed(2)}</p>
              </div>
              <motion.div whileTap={{ scale: 0.98 }}>
                <Button
                  className="rounded-full px-8 h-12 bg-foreground text-background hover:bg-foreground/90"
                  onClick={handleCheckout}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Checkout
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* Add padding at bottom for mobile fixed bar */}
      {cartItems.length > 0 && <div className="lg:hidden h-28" />}
    </div>
  );
};

export default CartPage;
